import axiosInstance from '@/lib/axios';

export type GiveAskType = 'GIVE' | 'ASK';
export type GiveAskStatus = 'OPEN' | 'FULFILLED';

export interface GiveAskItem {
  id: string;
  type: GiveAskType;
  title: string | null;
  description: string;
  businessName: string | null;
  category: string | null;
  subCategory: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  status: GiveAskStatus;
  fulfilledAt: string | null;
  createdAt: string;
}

export interface GiveAskOwner {
  id: string;
  firstName: string | null;
  lastName: string | null;
  image: string | null;
}

export interface GiveAskDirectoryItem extends GiveAskItem {
  owner: GiveAskOwner;
  bookmarked: boolean;
  hasReplied: boolean;
}

export interface GiveAskDirectoryResponse {
  items: GiveAskDirectoryItem[];
  page: number;
  limit: number;
  hasMore: boolean;
  currentUserId: string;
}

export interface GiveAskFilters {
  search: string;
  type: GiveAskType | null;
  status: 'OPEN' | 'ALL';
  category: string | null;
  saved: boolean;
  mine: boolean;
  page: number;
}

export interface GiveAskFacets {
  types: Record<GiveAskType, number>;
  categories: { category: string; count: number }[];
}

export interface MyCommunityGiveAsk extends GiveAskItem {
  enabled: boolean;
}

export interface GiveAskReplyItem {
  id: string;
  leadId: string | null;
  readAt: string | null;
  createdAt: string;
  communityId: string | null;
  eventId: string | null;
  fromUserId: string;
  fromFirstName: string | null;
  fromLastName: string | null;
  fromImage: string | null;
  leadTitle: string | null;
  leadDescription: string | null;
}

export interface ReplyPayload {
  communityId?: string | null;
  eventId?: string | null;
  beneficiary?: 'self' | 'other';
  beneficiaryName?: string;
  beneficiaryContact?: string | null;
  beneficiaryEmail?: string | null;
  beneficiaryRelation?: string | null;
  offeredGiveAskId?: string | null;
  manual?: {
    contactName?: string | null;
    businessName?: string | null;
    description: string;
    category?: string | null;
    subCategory?: string | null;
  } | null;
  message: string;
}

export interface FeedbackPayload {
  helpedByPlatform: 'YES' | 'MAYBE' | 'NO';
  feedback?: string | null;
}

export interface CreateGiveAskPayload {
  type: GiveAskType;
  title?: string | null;
  description: string;
  businessName?: string | null;
  category?: string | null;
  subCategory?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

export const giveAskApi = {

  directory: (communityId: string, filters: GiveAskFilters) =>
    axiosInstance
      .get<GiveAskDirectoryResponse>(`/api/communities/${communityId}/gives-asks`, {
        params: {
          page: filters.page,
          search: filters.search || undefined,
          type: filters.type ?? undefined,
          status: filters.status,
          category: filters.category ?? undefined,
          saved: filters.saved || undefined,
          mine: filters.mine || undefined,
        },
      })
      .then((r) => r.data),

  facets: (communityId: string) =>
    axiosInstance.get<GiveAskFacets>(`/api/communities/${communityId}/gives-asks/filters`).then((r) => r.data),

  listMyCommunityItems: (communityId: string) =>
    axiosInstance
      .get<{ items: MyCommunityGiveAsk[] }>(`/api/communities/${communityId}/gives-asks/me`)
      .then((r) => r.data),

  setCommunityEnabled: (communityId: string, enabledIds: string[]) =>
    axiosInstance.put(`/api/communities/${communityId}/gives-asks/me`, { enabledIds }).then((r) => r.data),

  createForCommunity: (communityId: string, payload: CreateGiveAskPayload) =>
    axiosInstance.post<{ id: string }>(`/api/communities/${communityId}/gives-asks/me`, payload).then((r) => r.data),

  listOwn: (type?: GiveAskType) =>
    axiosInstance
      .get<{ items: (GiveAskItem & { communityIds: string[] })[] }>('/api/user/gives-asks', {
        params: { type },
      })
      .then((r) => r.data),

  updateOwn: (giveAskId: string, payload: Partial<Omit<CreateGiveAskPayload, 'type'>>) =>
    axiosInstance.patch(`/api/user/gives-asks/${giveAskId}`, payload).then((r) => r.data),

  deleteOwn: (giveAskId: string) =>
    axiosInstance.delete(`/api/user/gives-asks/${giveAskId}`).then((r) => r.data),

  toggleBookmark: (giveAskId: string) =>
    axiosInstance.post<{ bookmarked: boolean }>(`/api/gives-asks/${giveAskId}/bookmark`).then((r) => r.data),

  reply: (giveAskId: string, payload: ReplyPayload) =>
    axiosInstance
      .post<{ success: true; leadId: string; replyId: string }>(`/api/gives-asks/${giveAskId}/replies`, payload)
      .then((r) => r.data),

  listReplies: (giveAskId: string) =>
    axiosInstance.get<{ replies: GiveAskReplyItem[] }>(`/api/gives-asks/${giveAskId}/replies`).then((r) => r.data),

  markReplyRead: (giveAskId: string, replyId: string) =>
    axiosInstance.post(`/api/gives-asks/${giveAskId}/replies/${replyId}/read`),

  fulfil: (giveAskId: string) => axiosInstance.post(`/api/user/gives-asks/${giveAskId}/fulfil`),

  submitFeedback: (giveAskId: string, payload: FeedbackPayload) =>
    axiosInstance.post(`/api/user/gives-asks/${giveAskId}/fulfil/feedback`, payload),
};

export function giveAskHeading(item: {
  type: GiveAskType;
  title: string | null;
  businessName: string | null;
  contactName: string | null;
  description: string;
}): string {
  if (item.type === 'ASK') return item.title ?? '';
  return item.businessName || item.contactName || item.description;
}
